import { useCreateSavedScreen } from "../../api/hooks/useCreateSavedScreen";
import { useUpdateSavedScreen } from "../../api/hooks/useUpdateSavedScreen";
import {
  PredicateBuilderDialog,
  type PredicateMutationLike,
} from "../predicates/PredicateBuilderDialog";

/**
 * SavedScreen builder dialog -- thin wrapper around the shared
 * `PredicateBuilderDialog`. In create mode it POSTs a new SavedScreen via
 * `useCreateSavedScreen`; when `editing` is supplied it seeds the builder from
 * that filter's name + criteria and PATCHes it via `useUpdateSavedScreen`, so
 * the same surface both *shows* an existing filter's conditions and lets the
 * user edit + save them (issue 7).
 */
export type ScreenBuilderEditing = {
  saved_screen_id: string;
  name: string;
  criteria: Record<string, unknown>;
};

export type ScreenBuilderDialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When set, the dialog edits this existing filter instead of creating one. */
  editing?: ScreenBuilderEditing | null;
};

export function ScreenBuilderDialog({
  open,
  onOpenChange,
  editing,
}: ScreenBuilderDialogProps = {}) {
  const createMutation = useCreateSavedScreen();
  const updateMutation = useUpdateSavedScreen();
  const isEditing = editing != null;

  const editMutation: PredicateMutationLike = {
    mutate: ({ name, criteria }, options) =>
      updateMutation.mutate(
        {
          saved_screen_id: editing?.saved_screen_id ?? "",
          body: { name, criteria },
        },
        options,
      ),
    isPending: updateMutation.isPending,
    isError: updateMutation.isError,
    reset: updateMutation.reset,
  };

  return (
    <PredicateBuilderDialog
      open={open}
      onOpenChange={onOpenChange}
      triggerLabel="+ New filter"
      dialogAriaLabel={isEditing ? "Edit saved filter" : "Create saved filter"}
      title={isEditing ? "Edit filter" : "Create filter"}
      description="Combine conditions with AND / OR / NOT. Leave empty to match every trade."
      nameLabel="Name"
      idPrefix={isEditing ? "edit-screen" : "create-screen"}
      errorMessage="Failed to save filter. Check the field / operator / value."
      submitLabel={isEditing ? "Save changes" : "Save"}
      mutation={isEditing ? editMutation : createMutation}
      initialName={editing?.name}
      initialCriteria={editing?.criteria ?? null}
    />
  );
}
